// Aba/painel "Arquivos". Explorer read/write dos shares do servidor de arquivos
// (SETORES$/PUBLICA$), servido pelo backend /api/files/* → smb-gateway no host.
// Admin-only (gate no App e no backend). 3 modos de visão (lista, grade pequena,
// grade grande com thumbnail), ícones por tipo + preview (vídeo/áudio/pdf/
// planilha/word/imagem/texto). Sem dependências novas (xlsx/mammoth já existem).
import { useState, useEffect, useCallback, useRef } from 'react'
import type { ComponentType } from 'react'
import {
  FiDownload, FiUpload, FiChevronRight, FiEdit,
  FiHome, FiRefreshCw, FiLoader, FiX, FiList, FiGrid, FiImage,
  FiClock, FiRotateCcw, FiMonitor, FiGlobe, FiSearch,
} from 'react-icons/fi'
import { FaFolder } from 'react-icons/fa6'
import {
  FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage,
  FaFileVideo, FaFileAudio, FaFileZipper, FaFileCsv, FaFileCode,
  FaFileLines, FaFile,
} from 'react-icons/fa6'
import { API_ENDPOINTS } from '../lib/api'

type Entry = { name: string; isDir: boolean; size: number; mtime: number }
type SearchHit = Entry & { path: string }
type Fetch = (url: string, options?: RequestInit) => Promise<Response>
type ViewMode = 'list' | 'grid-sm' | 'grid-lg'
interface Props { authenticatedFetch: Fetch }

// ── classificação por extensão ────────────────────────────────────────────
type Kind = 'image' | 'video' | 'audio' | 'pdf' | 'excel' | 'word' | 'ppt'
  | 'csv' | 'code' | 'text' | 'archive' | 'other'

const EXT: Record<string, Kind> = {
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', bmp: 'image', svg: 'image', tiff: 'image', ico: 'image',
  mp4: 'video', webm: 'video', mov: 'video', avi: 'video', mkv: 'video', wmv: 'video', flv: 'video', m4v: 'video',
  mp3: 'audio', wav: 'audio', ogg: 'audio', m4a: 'audio', flac: 'audio', aac: 'audio', opus: 'audio',
  pdf: 'pdf',
  xls: 'excel', xlsx: 'excel', ods: 'excel',
  csv: 'csv',
  doc: 'word', docx: 'word', rtf: 'word', odt: 'word',
  ppt: 'ppt', pptx: 'ppt', odp: 'ppt',
  zip: 'archive', rar: 'archive', '7z': 'archive', tar: 'archive', gz: 'archive',
  js: 'code', ts: 'code', tsx: 'code', jsx: 'code', json: 'code', xml: 'code', html: 'code', css: 'code', py: 'code', sql: 'code', sh: 'code', cjs: 'code', mjs: 'code', yml: 'code', yaml: 'code',
  txt: 'text', md: 'text', log: 'text', ini: 'text', cfg: 'text',
}
const ICON: Record<Kind, { Icon: ComponentType<{ className?: string }>; color: string }> = {
  image: { Icon: FaFileImage, color: 'text-purple-500' },
  video: { Icon: FaFileVideo, color: 'text-rose-500' },
  audio: { Icon: FaFileAudio, color: 'text-amber-500' },
  pdf: { Icon: FaFilePdf, color: 'text-red-500' },
  excel: { Icon: FaFileExcel, color: 'text-green-600' },
  word: { Icon: FaFileWord, color: 'text-blue-600' },
  ppt: { Icon: FaFilePowerpoint, color: 'text-orange-500' },
  csv: { Icon: FaFileCsv, color: 'text-green-600' },
  code: { Icon: FaFileCode, color: 'text-cyan-500' },
  text: { Icon: FaFileLines, color: 'text-slate-500' },
  archive: { Icon: FaFileZipper, color: 'text-yellow-500' },
  other: { Icon: FaFile, color: 'text-muted-foreground' },
}
const MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', ico: 'image/x-icon', tiff: 'image/tiff',
  mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', mkv: 'video/x-matroska', avi: 'video/x-msvideo', wmv: 'video/x-ms-wmv', flv: 'video/x-flv',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4', flac: 'audio/flac', aac: 'audio/aac', opus: 'audio/ogg',
}
const extOf = (name: string) => (name.includes('.') ? name.split('.').pop()!.toLowerCase() : '')
const kindOf = (name: string): Kind => EXT[extOf(name)] || 'other'
const mimeFor = (name: string) => MIME[extOf(name)] || 'application/octet-stream'
const previewable = (k: Kind) => k !== 'archive' && k !== 'ppt' && k !== 'other'
// Formatos editáveis online via OnlyOffice
const OO_EDIT = new Set(['docx', 'doc', 'odt', 'rtf', 'xlsx', 'xls', 'ods', 'csv', 'pptx', 'ppt', 'odp'])
const ooEditable = (name: string) => OO_EDIT.has(extOf(name))

function fmtSize(n: number): string {
  if (!n) return '—'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`
}
function fmtDate(ts: number): string {
  if (!ts) return '—'
  try { return new Date(ts * 1000).toLocaleDateString('pt-BR') } catch { return '—' }
}
// objectURL com MIME correto (crucial: PDF em octet-stream não renderiza, baixa).
async function typedBlobUrl(authedFetch: Fetch, fullPath: string, name: string): Promise<string> {
  const r = await authedFetch(`${API_ENDPOINTS.FILES_DOWNLOAD}?path=${encodeURIComponent(fullPath)}`)
  if (!r.ok) { const t = await r.json().catch(() => ({})); throw new Error(t.error || `HTTP ${r.status}`) }
  const buf = await r.arrayBuffer()
  return URL.createObjectURL(new Blob([buf], { type: mimeFor(name) }))
}

export function ArquivosLab({ authenticatedFetch }: Props) {
  const [path, setPath] = useState('')
  const [entries, setEntries] = useState<Entry[]>([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<{ path: string; name: string } | null>(null)
  const [editing, setEditing] = useState<{ path: string; name: string } | null>(null)
  const [history, setHistory] = useState<{ path: string; name: string } | null>(null)
  const [view, setView] = useState<ViewMode>(() => {
    try { return (localStorage.getItem('arquivos-view') as ViewMode) || 'list' } catch { return 'list' }
  })
  // Busca por nome (recursiva a partir da pasta atual; Enter dispara — não
  // busca a cada tecla). results !== null = modo busca ativo no corpo.
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchHit[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [searchTruncated, setSearchTruncated] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const reqId = useRef(0)

  const segments = path ? path.split('/') : []
  const atRoot = segments.length === 0

  const changeView = (v: ViewMode) => { setView(v); try { localStorage.setItem('arquivos-view', v) } catch { /* */ } }

  const load = useCallback(async (p: string) => {
    const id = ++reqId.current
    setLoading(true); setError(null)
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.FILES_LIST}?path=${encodeURIComponent(p)}`)
      if (id !== reqId.current) return
      if (r.status === 403) { setError('Acesso restrito a administradores.'); setEntries([]); return }
      const data = await r.json()
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setEntries(data.entries || [])
      setTruncated(!!data.truncated)
    } catch (e) {
      if (id !== reqId.current) return
      setError((e as Error).message); setEntries([])
    } finally {
      if (id === reqId.current) setLoading(false)
    }
  }, [authenticatedFetch])

  useEffect(() => { load(path) }, [path, load])
  // Navegar limpa o modo busca (o corpo volta pra listagem da pasta).
  useEffect(() => { setResults(null); setSearchTruncated(false) }, [path])

  const enter = (name: string) => setPath(path ? `${path}/${name}` : name)
  const goTo = (idx: number) => setPath(segments.slice(0, idx + 1).join('/'))
  const fullPath = (name: string) => (path ? `${path}/${name}` : name)
  // Formatos do OnlyOffice abrem direto no editor (modo edit/view decidido pelo
  // backend conforme permissão). Demais: preview inline ou download.
  const openAt = (full: string, name: string) =>
    ooEditable(name) ? setEditing({ path: full, name })
    : previewable(kindOf(name)) ? setPreview({ path: full, name })
    : downloadAt(full, name)
  const openFile = (it: Entry) => openAt(fullPath(it.name), it.name)

  const downloadAt = async (full: string, name: string) => {
    try {
      const url = await typedBlobUrl(authenticatedFetch, full, name)
      const a = document.createElement('a')
      a.href = url; a.download = name; document.body.appendChild(a); a.click()
      a.remove(); URL.revokeObjectURL(url)
    } catch (e) {
      setError(`Falha ao baixar "${name}": ${(e as Error).message}`)
    }
  }
  const download = (name: string) => downloadAt(fullPath(name), name)

  const runSearch = async () => {
    const q = query.trim()
    if (q.length < 2) return
    setSearching(true); setError(null)
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.FILES_SEARCH}?path=${encodeURIComponent(path)}&q=${encodeURIComponent(q)}`)
      const data = await r.json()
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setResults(data.results || [])
      setSearchTruncated(!!data.truncated)
    } catch (e) {
      setError(`Falha na busca: ${(e as Error).message}`); setResults(null)
    } finally {
      setSearching(false)
    }
  }
  const clearSearch = () => { setQuery(''); setResults(null); setSearchTruncated(false) }

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) e.target.value = ''
    if (!file) return
    setUploading(true); setError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await authenticatedFetch(`${API_ENDPOINTS.FILES_UPLOAD}?path=${encodeURIComponent(path)}`, { method: 'POST', body: fd })
      const data = await r.json()
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      await load(path)
    } catch (err) {
      setError(`Falha ao enviar "${file.name}": ${(err as Error).message}`)
    } finally {
      setUploading(false)
    }
  }

  const VIEWS: Array<{ id: ViewMode; Icon: ComponentType<{ className?: string }>; title: string }> = [
    { id: 'list', Icon: FiList, title: 'Lista' },
    { id: 'grid-sm', Icon: FiGrid, title: 'Grade — ícones pequenos' },
    { id: 'grid-lg', Icon: FiImage, title: 'Grade — ícones grandes com pré-visualização' },
  ]

  return (
    <div className="flex flex-col h-full min-h-0 glass-panel neu-canvas neu-forms pln-arquivos rounded-xl overflow-hidden card-shadow">
      {/* Cabeçalho: breadcrumb + ações */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 shrink-0">
        <div className="flex items-center gap-1 text-sm min-w-0 overflow-x-auto flex-1">
          <button onClick={() => setPath('')} title="Início (raiz dos compartilhamentos)"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground shrink-0">
            <FiHome className="w-4 h-4" />
          </button>
          {segments.map((seg, i) => (
            <span key={i} className="inline-flex items-center gap-1 min-w-0">
              <FiChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <button onClick={() => goTo(i)} title={seg}
                className={`truncate max-w-[140px] ${i === segments.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                {seg}
              </button>
            </span>
          ))}
        </div>
        {/* busca por nome (Enter dispara; escopo = pasta atual) */}
        <div className="relative shrink-0 w-40 sm:w-56">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runSearch(); if (e.key === 'Escape') clearSearch() }}
            placeholder={atRoot ? 'Buscar por nome…' : 'Buscar nesta pasta…'}
            title="Busca recursiva por nome a partir da pasta atual (Enter busca, Esc limpa)"
            className="w-full pl-8 pr-7 py-1.5 rounded-lg text-[13px] bg-muted/40 border border-border/50 focus:border-primary/50 focus:outline-none placeholder:text-muted-foreground/70"
          />
          {(query || results !== null) && (
            <button onClick={clearSearch} title="Limpar busca"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground">
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* alternância de visão */}
        <div className="glass-panel neu-seg rounded-full p-0.5 flex gap-0.5 shrink-0">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => changeView(v.id)} title={v.title}
              className={`p-1.5 rounded-full transition-colors ${view === v.id ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <v.Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
        <button onClick={() => load(path)} title="Atualizar" disabled={loading}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 shrink-0">
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {!atRoot && (
          <>
            <input ref={fileInput} type="file" className="hidden" onChange={onUpload} />
            <button onClick={() => fileInput.current?.click()} disabled={uploading}
              title="Enviar arquivo para esta pasta"
              className="neu-relief inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium bg-primary text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-60 shrink-0">
              {uploading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
              <span className="hidden sm:inline">{uploading ? 'Enviando…' : 'Enviar'}</span>
            </button>
          </>
        )}
      </div>

      {/* Corpo */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {error && <div className="neu-relief m-4 px-4 py-3 rounded-xl bg-destructive/10 text-destructive text-sm">{error}</div>}
        {truncated && (
          <div className="mx-4 mt-4 px-4 py-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs">
            Pasta grande: exibindo os primeiros 5000 itens.
          </div>
        )}
        {loading && results === null && entries.length === 0 && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        )}
        {!loading && !error && results === null && entries.length === 0 && (
          <div className="py-16 text-center text-muted-foreground text-sm">Pasta vazia.</div>
        )}

        {/* RESULTADOS DA BUSCA */}
        {searching && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Buscando…
          </div>
        )}
        {!searching && results !== null && (
          <>
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground border-b border-border/40">
              <FiSearch className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 min-w-0 truncate">
                {results.length} resultado{results.length === 1 ? '' : 's'} para “{query.trim()}”
                {path ? <> em <span className="text-foreground/80">{path}</span></> : ' em todos os compartilhamentos'}
                {searchTruncated && ' — busca parcial (muitos itens); refine o termo ou busque numa subpasta'}
              </span>
              <button onClick={clearSearch} title="Voltar à navegação normal"
                className="shrink-0 px-2 py-1 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground">Limpar</button>
            </div>
            {results.length === 0 && (
              <div className="py-16 text-center text-muted-foreground text-sm">Nada encontrado com esse nome.</div>
            )}
            <ul className="divide-y divide-border/40">
              {results.map((it) => {
                const parent = it.path.includes('/') ? it.path.slice(0, it.path.lastIndexOf('/')) : ''
                return it.isDir ? (
                  <li key={`sd-${it.path}`} className="animate-file-fade">
                    <button onClick={() => setPath(it.path)} title={`Abrir pasta ${it.path}`}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left neu-rule-2 hover:bg-muted/40 transition-colors">
                      <FaFolder className="w-5 h-5 text-amber-400 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm text-foreground">{it.name}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">{parent || 'raiz'}</span>
                      </span>
                      <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </button>
                  </li>
                ) : (
                  <li key={`sf-${it.path}`} className="animate-file-fade">
                    <div className="w-full flex items-center gap-3 px-4 py-2.5 neu-rule-2 hover:bg-muted/40 transition-colors">
                      {(() => { const { Icon, color } = ICON[kindOf(it.name)]; return <Icon className={`w-5 h-5 shrink-0 ${color}`} /> })()}
                      <button onClick={() => openAt(it.path, it.name)}
                        title={ooEditable(it.name) ? `Abrir ${it.name} no editor` : previewable(kindOf(it.name)) ? `Visualizar ${it.name}` : `Baixar ${it.name}`}
                        className="flex-1 min-w-0 text-left hover:text-primary">
                        <span className="block truncate text-sm text-foreground">{it.name}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">{parent || 'raiz'}</span>
                      </button>
                      <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-20 text-right" title="Tamanho">{fmtSize(it.size)}</span>
                      <span className="hidden md:block text-xs text-muted-foreground tabular-nums shrink-0 w-24 text-right" title="Modificado em">{fmtDate(it.mtime)}</span>
                      <button onClick={() => setHistory({ path: it.path, name: it.name })} title={`Histórico de ${it.name}`}
                        className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-primary hover:bg-muted/60 shrink-0">
                        <FiClock className="w-4 h-4" />
                      </button>
                      <button onClick={() => downloadAt(it.path, it.name)} title={`Baixar ${it.name}`}
                        className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-primary hover:bg-muted/60 shrink-0">
                        <FiDownload className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        )}

        {/* LISTA */}
        {results === null && !searching && entries.length > 0 && view === 'list' && (
          <ul className="divide-y divide-border/40">
            {entries.map((it) => it.isDir ? (
              <li key={`d-${it.name}`} className="animate-file-fade">
                <button onClick={() => enter(it.name)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left neu-rule-2 hover:bg-muted/40 transition-colors">
                  <FaFolder className="w-5 h-5 text-amber-400 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-sm text-foreground">{it.name}</span>
                  <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              </li>
            ) : (
              <li key={`f-${it.name}`} className="animate-file-fade">
                <div className="w-full flex items-center gap-3 px-4 py-2.5 neu-rule-2 hover:bg-muted/40 transition-colors">
                  {(() => { const { Icon, color } = ICON[kindOf(it.name)]; return <Icon className={`w-5 h-5 shrink-0 ${color}`} /> })()}
                  <button onClick={() => openFile(it)} title={ooEditable(it.name) ? `Abrir ${it.name} no editor` : previewable(kindOf(it.name)) ? `Visualizar ${it.name}` : `Baixar ${it.name}`}
                    className="flex-1 min-w-0 truncate text-sm text-foreground text-left hover:text-primary">{it.name}</button>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-20 text-right" title="Tamanho">{fmtSize(it.size)}</span>
                  <span className="hidden md:block text-xs text-muted-foreground tabular-nums shrink-0 w-24 text-right" title="Modificado em">{fmtDate(it.mtime)}</span>
                  <button onClick={() => setHistory({ path: fullPath(it.name), name: it.name })} title={`Histórico de ${it.name}`}
                    className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-primary hover:bg-muted/60 shrink-0">
                    <FiClock className="w-4 h-4" />
                  </button>
                  <button onClick={() => download(it.name)} title={`Baixar ${it.name}`}
                    className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-primary hover:bg-muted/60 shrink-0">
                    <FiDownload className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* GRADE */}
        {results === null && !searching && entries.length > 0 && view !== 'list' && (
          <div className={`grid gap-3 p-4 ${view === 'grid-sm'
            ? 'grid-cols-[repeat(auto-fill,minmax(110px,1fr))]'
            : 'grid-cols-[repeat(auto-fill,minmax(170px,1fr))]'}`}>
            {entries.map((it) => (
              <Tile key={`${it.isDir ? 'd' : 'f'}-${it.name}`} entry={it} large={view === 'grid-lg'}
                authenticatedFetch={authenticatedFetch} fullPath={fullPath(it.name)}
                onOpen={() => it.isDir ? enter(it.name) : openFile(it)}
                onDownload={() => download(it.name)}
                onHistory={() => setHistory({ path: fullPath(it.name), name: it.name })} />
            ))}
          </div>
        )}
      </div>

      {preview && (
        <FilePreview authenticatedFetch={authenticatedFetch} path={preview.path} name={preview.name}
          onClose={() => setPreview(null)} onDownload={() => download(preview.name)} />
      )}
      {editing && (
        <OnlyOfficeEditor authenticatedFetch={authenticatedFetch} path={editing.path} name={editing.name}
          onClose={() => setEditing(null)} />
      )}
      {history && (
        <FileHistory authenticatedFetch={authenticatedFetch} path={history.path} name={history.name}
          onClose={() => setHistory(null)} onRestored={() => load(path)} />
      )}
    </div>
  )
}

// ── Editor OnlyOffice (modal full-screen) ───────────────────────────────────
// Carrega o api.js de docs.twms.com.br e instancia o DocEditor com a config
// vinda do backend (que assina o token e aponta document.url/callbackUrl pro
// próprio backend). Salvar volta pro share via callback. Requer DNS de
// docs.twms.com.br resolvido (senão o api.js não carrega).
function loadOoScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[data-oo="1"]`)) return resolve()
    const s = document.createElement('script')
    s.src = src; s.async = true; s.dataset.oo = '1'
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('não foi possível carregar o editor (docs.twms.com.br acessível?)'))
    document.head.appendChild(s)
  })
}

function OnlyOfficeEditor({ authenticatedFetch, path, name, onClose }: {
  authenticatedFetch: Fetch; path: string; name: string; onClose: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const containerId = useRef(`oo-${Math.random().toString(36).slice(2)}`)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authenticatedFetch(`${API_ENDPOINTS.FILES_EDIT_CONFIG}?path=${encodeURIComponent(path)}`)
        const d = await r.json()
        if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
        await loadOoScript(d.apiUrl)
        if (cancelled) return
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const DocsAPI = (window as any).DocsAPI
        if (!DocsAPI) throw new Error('editor indisponível (docs.twms.com.br)')
        editorRef.current = new DocsAPI.DocEditor(containerId.current, {
          ...d.config,
          width: '100%', height: '100%',
          events: {
            onDocumentReady: () => { if (!cancelled) setLoading(false) },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (e: any) => { if (!cancelled) { setError('Erro do editor: ' + (e?.data ?? '')); setLoading(false) } },
          },
        })
      } catch (e) {
        if (!cancelled) { setError((e as Error).message); setLoading(false) }
      }
    })()
    return () => {
      cancelled = true
      try { editorRef.current?.destroyEditor?.() } catch { /* ignore */ }
    }
  }, [path, authenticatedFetch])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-neutral-900">
      <div className="flex items-center gap-3 px-4 py-2 text-white shrink-0">
        <FiEdit className="w-4 h-4 shrink-0" />
        <span className="flex-1 min-w-0 truncate text-sm font-medium" title={name}>{name}</span>
        <button onClick={onClose} title="Fechar (salva automaticamente)" className="p-2 rounded-lg hover:bg-white/15"><FiX className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 min-h-0 relative bg-white">
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center bg-background">
            <p className="text-sm text-destructive">{error}</p>
            <button onClick={onClose} className="neu-relief px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground">Fechar</button>
          </div>
        )}
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-muted-foreground text-sm bg-background">
            <FiLoader className="w-5 h-5 animate-spin" /> Abrindo editor…
          </div>
        )}
        <div id={containerId.current} className="w-full h-full" />
      </div>
    </div>
  )
}

// ── Modal de histórico de versões (web + Windows + restore) ─────────────────
type Ver = { id: number; version: number; source: string; author: string | null; size: number; createdAt: string; sha: string }
const VER_SRC: Record<string, { label: string; Icon: ComponentType<{ className?: string; title?: string }>; cls: string }> = {
  web: { label: 'Web (editor)', Icon: FiGlobe, cls: 'text-sky-500' },
  windows: { label: 'Windows', Icon: FiMonitor, cls: 'text-blue-400' },
  restore: { label: 'Restauração', Icon: FiRotateCcw, cls: 'text-amber-500' },
  vss: { label: 'Snapshot', Icon: FiClock, cls: 'text-violet-400' },
}
function FileHistory({ authenticatedFetch, path, name, onClose, onRestored }: {
  authenticatedFetch: Fetch; path: string; name: string; onClose: () => void; onRestored: () => void
}) {
  const [versions, setVersions] = useState<Ver[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  const loadVersions = useCallback(async () => {
    setError(null)
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.FILES_VERSIONS}?path=${encodeURIComponent(path)}`)
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      setVersions(d.versions)
    } catch (e) { setError((e as Error).message); setVersions([]) }
  }, [authenticatedFetch, path])
  useEffect(() => { loadVersions() }, [loadVersions])

  const downloadVer = async (v: Ver) => {
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.FILES_VERSION_CONTENT}?path=${encodeURIComponent(path)}&id=${v.id}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const url = URL.createObjectURL(new Blob([await r.arrayBuffer()], { type: mimeFor(name) }))
      const a = document.createElement('a')
      a.href = url; a.download = `v${v.version}_${name}`; document.body.appendChild(a); a.click()
      a.remove(); URL.revokeObjectURL(url)
    } catch (e) { setError(`Falha ao baixar versão: ${(e as Error).message}`) }
  }
  const restore = async (v: Ver) => {
    if (!window.confirm(`Restaurar a versão ${v.version} de "${name}"? O conteúdo atual vira uma nova versão no histórico — dá pra desfazer.`)) return
    setBusy(v.id); setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.FILES_RESTORE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, id: v.id }),
      })
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      onRestored()
      await loadVersions()
    } catch (e) { setError(`Falha ao restaurar: ${(e as Error).message}`) }
    finally { setBusy(null) }
  }
  const fmtWhen = (s: string) => { try { return new Date(s).toLocaleString('pt-BR') } catch { return s } }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/95 p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl bg-background border border-border shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
          <FiClock className="w-4 h-4 text-primary shrink-0" />
          <span className="flex-1 min-w-0 truncate text-sm font-medium" title={name}>Histórico — {name}</span>
          <button onClick={onClose} title="Fechar" className="p-1.5 rounded-lg hover:bg-muted/60"><FiX className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {error && <div className="neu-relief m-3 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-xs">{error}</div>}
          {versions === null && (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground text-sm">
              <FiLoader className="w-4 h-4 animate-spin" /> Carregando histórico…
            </div>
          )}
          {versions && versions.length === 0 && !error && (
            <div className="py-12 text-center text-muted-foreground text-sm px-6">
              Sem versões guardadas ainda. A primeira aparece ao salvar pelo editor web ou na próxima vez que o arquivo mudar via Windows.
            </div>
          )}
          {versions && versions.length > 0 && (
            <ul className="divide-y divide-border/40">
              {versions.map((v, i) => {
                const s = VER_SRC[v.source] || VER_SRC.web
                return (
                  <li key={v.id} className="flex items-center gap-3 px-4 py-2.5">
                    <s.Icon className={`w-4 h-4 shrink-0 ${s.cls}`} title={s.label} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-foreground truncate">
                        v{v.version}
                        {i === 0 && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary align-middle">atual</span>}
                        <span className="ml-2 text-xs text-muted-foreground">{s.label}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {fmtWhen(v.createdAt)} · {fmtSize(v.size)}{v.author ? ` · ${v.author}` : ''}
                      </div>
                    </div>
                    <button onClick={() => downloadVer(v)} title="Baixar esta versão"
                      className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-primary hover:bg-muted/60 shrink-0">
                      <FiDownload className="w-4 h-4" />
                    </button>
                    {i !== 0 && (
                      <button onClick={() => restore(v)} disabled={busy !== null} title="Restaurar esta versão"
                        className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-amber-500 hover:bg-muted/60 shrink-0 disabled:opacity-50">
                        {busy === v.id ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiRotateCcw className="w-4 h-4" />}
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Tile da grade (com thumbnail lazy de imagem no modo grande) ─────────────
function Tile({ entry, large, authenticatedFetch, fullPath, onOpen, onDownload, onHistory }: {
  entry: Entry; large: boolean; authenticatedFetch: Fetch; fullPath: string
  onOpen: () => void; onDownload: () => void; onHistory: () => void
}) {
  const kind = kindOf(entry.name)
  const [thumb, setThumb] = useState<string | null>(null)
  const ref = useRef<HTMLButtonElement>(null)

  // No modo grande, imagens carregam thumbnail real quando entram na viewport.
  useEffect(() => {
    if (!large || entry.isDir || kind !== 'image') return
    const el = ref.current
    if (!el) return
    let url: string | null = null, cancelled = false
    const obs = new IntersectionObserver((es) => {
      if (es[0].isIntersecting) {
        obs.disconnect()
        typedBlobUrl(authenticatedFetch, fullPath, entry.name)
          .then(u => { if (cancelled) { URL.revokeObjectURL(u); return } url = u; setThumb(u) })
          .catch(() => { /* mantém o ícone */ })
      }
    }, { rootMargin: '300px' })
    obs.observe(el)
    return () => { cancelled = true; obs.disconnect(); if (url) URL.revokeObjectURL(url) }
  }, [large, entry.isDir, entry.name, kind, fullPath, authenticatedFetch])

  const box = large ? 'h-28' : 'h-14'
  const iconSize = large ? 'w-12 h-12' : 'w-7 h-7'

  return (
    <div className="group relative animate-file-fade">
      <button ref={ref} onClick={onOpen} title={entry.name}
        className="w-full flex flex-col items-center gap-2 p-3 rounded-xl neu-item transition-all">
        <div className={`${box} w-full flex items-center justify-center rounded-lg overflow-hidden ${thumb ? 'bg-black/5 dark:bg-white/5' : ''}`}>
          {entry.isDir
            ? <FaFolder className={`${iconSize} text-amber-400`} />
            : thumb
              ? <img src={thumb} alt={entry.name} className="max-w-full max-h-full object-contain" />
              : (() => { const { Icon, color } = ICON[kind]; return <Icon className={`${iconSize} ${color}`} /> })()}
        </div>
        <span className="w-full text-center text-xs text-foreground line-clamp-2 break-words leading-tight">{entry.name}</span>
      </button>
      {!entry.isDir && (
        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity">
          <button onClick={onHistory} title={`Histórico de ${entry.name}`}
            className="p-1.5 rounded-lg bg-background/80 text-muted-foreground hover:text-primary">
            <FiClock className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDownload} title={`Baixar ${entry.name}`}
            className="p-1.5 rounded-lg bg-background/80 text-muted-foreground hover:text-primary">
            <FiDownload className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}

// ── Modal de preview ───────────────────────────────────────────────────────
function FilePreview({ authenticatedFetch, path, name, onClose, onDownload }: {
  authenticatedFetch: Fetch; path: string; name: string; onClose: () => void; onDownload: () => void
}) {
  const kind = kindOf(name)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [sheet, setSheet] = useState<{ names: string[]; active: number; rows: string[][] } | null>(null)
  const renderSheetRef = useRef<((i: number) => string[][]) | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let url: string | null = null, cancelled = false
    ;(async () => {
      setLoading(true); setError(null)
      try {
        if (kind === 'image' || kind === 'video' || kind === 'audio' || kind === 'pdf') {
          url = await typedBlobUrl(authenticatedFetch, path, name)
          if (cancelled) { URL.revokeObjectURL(url); return }
          setBlobUrl(url)
        } else {
          const r = await authenticatedFetch(`${API_ENDPOINTS.FILES_DOWNLOAD}?path=${encodeURIComponent(path)}`)
          if (!r.ok) { const t = await r.json().catch(() => ({})); throw new Error(t.error || `HTTP ${r.status}`) }
          const blob = await r.blob()
          if (cancelled) return
          if (kind === 'text' || kind === 'code') {
            setText((await blob.text()).slice(0, 500_000))
          } else if (kind === 'excel' || kind === 'csv') {
            const XLSX = await import('xlsx')
            const wb = XLSX.read(await blob.arrayBuffer(), { type: 'array' })
            renderSheetRef.current = (i: number) => {
              const ws = wb.Sheets[wb.SheetNames[i]]
              const rows = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' }) as string[][]
              return rows.slice(0, 1000).map(row => row.slice(0, 50).map(c => String(c ?? '')))
            }
            if (!cancelled) setSheet({ names: wb.SheetNames, active: 0, rows: renderSheetRef.current(0) })
          } else if (kind === 'word') {
            if (extOf(name) !== 'docx') throw new Error('Pré-visualização disponível só para .docx — baixe o arquivo.')
            const mammoth = await import('mammoth')
            const res = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() })
            if (!cancelled) setHtml(res.value)
          }
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url) }
  }, [path, name, kind, authenticatedFetch])

  const switchSheet = (i: number) => {
    if (renderSheetRef.current && sheet) setSheet({ ...sheet, active: i, rows: renderSheetRef.current(i) })
  }

  const { Icon, color } = ICON[kind]
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-neutral-900 animate-file-fade" onClick={onClose}>
      <div className="flex items-center gap-3 px-4 py-3 text-white shrink-0" onClick={e => e.stopPropagation()}>
        <Icon className={`w-5 h-5 shrink-0 ${color}`} />
        <span className="flex-1 min-w-0 truncate text-sm font-medium" title={name}>{name}</span>
        <button onClick={onDownload} title="Baixar"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] bg-white/15 hover:bg-white/25">
          <FiDownload className="w-4 h-4" /><span className="hidden sm:inline">Baixar</span>
        </button>
        <button onClick={onClose} title="Fechar (Esc)" className="p-2 rounded-lg hover:bg-white/15"><FiX className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 min-h-0 p-4 pt-0 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="w-full h-full rounded-xl bg-background overflow-hidden flex flex-col animate-file-pop">
          {loading && (
            <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground text-sm">
              <FiLoader className="w-5 h-5 animate-spin" /> Carregando pré-visualização…
            </div>
          )}
          {error && !loading && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-destructive">{error}</p>
              <button onClick={onDownload} className="neu-relief inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground">
                <FiDownload className="w-4 h-4" /> Baixar arquivo
              </button>
            </div>
          )}
          {!loading && !error && (
            <>
              {kind === 'image' && blobUrl && (
                <div className="flex-1 flex items-center justify-center overflow-auto p-4">
                  <img src={blobUrl} alt={name} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              {kind === 'video' && blobUrl && (
                <div className="flex-1 flex items-center justify-center bg-black">
                  <video src={blobUrl} controls autoPlay className="max-w-full max-h-full" />
                </div>
              )}
              {kind === 'audio' && blobUrl && (
                <div className="flex-1 flex items-center justify-center p-8">
                  <audio src={blobUrl} controls autoPlay className="w-full max-w-xl" />
                </div>
              )}
              {kind === 'pdf' && blobUrl && (
                <iframe src={blobUrl} title={name} className="flex-1 w-full border-0" />
              )}
              {(kind === 'text' || kind === 'code') && text != null && (
                <pre className="flex-1 overflow-auto p-4 text-xs leading-relaxed text-foreground whitespace-pre-wrap break-words">{text}</pre>
              )}
              {kind === 'word' && html != null && (
                <div className="flex-1 overflow-auto p-6 prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
              )}
              {(kind === 'excel' || kind === 'csv') && sheet && (
                <div className="flex-1 flex flex-col min-h-0">
                  {sheet.names.length > 1 && (
                    <div className="flex gap-1 px-3 pt-3 overflow-x-auto shrink-0">
                      {sheet.names.map((n, i) => (
                        <button key={n} onClick={() => switchSheet(i)}
                          className={`px-3 py-1 rounded-t-lg text-xs whitespace-nowrap ${i === sheet.active ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex-1 overflow-auto p-3">
                    <table className="text-xs border-collapse">
                      <tbody>
                        {sheet.rows.map((row, ri) => (
                          <tr key={ri} className={ri === 0 ? 'font-semibold bg-muted/50' : ''}>
                            {row.map((cell, ci) => (
                              <td key={ci} className="border border-border/50 px-2 py-1 whitespace-nowrap max-w-[280px] truncate">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
